import * as React from 'react';
import { styled } from '@linaria/react';

import {
  useNewsletterSignup,
  useConfetti,
} from '@/helpers/newsletter.helpers';

import MaxWidthWrapper from '@/components/MaxWidthWrapper';
import TextInput from '@/components/TextInput';
import JoshMascot from '@/components/JoshMascot';
import Spinner from '@/components/Spinner';
import Paragraph from '@/components/Paragraph';
import Spicy from '@/components/Spicy';
import ButtonPoofy from '@/components/ButtonPoofy';
import SubscribeGeyser from '@/components/SubscribeGeyser';
import SubscribeError from '@/components/SubscribeError';

function GeneratorNewsletterSignup() {
  const successRef = React.useRef<HTMLDivElement>(null);

  const {
    confettiStatus,
    handleLoadConfetti,
    handleTriggerConfetti,
  } = useConfetti();

  const {
    status,
    emailFields,
    error,
    numOfEncounteredErrors,
    emailId,
    honeypotElem,
    handleSubmit,
  } = useNewsletterSignup({
    tags: ['primaryNewsletter'],
    formName: 'primaryNewsletter',
    callbacks: {
      onStart: handleLoadConfetti,
      onSuccess: handleTriggerConfetti,
    },
  });

  React.useEffect(() => {
    if (status === 'success') {
      successRef.current?.scrollIntoView();
    }
  }, [status]);

  return (
    <Wrapper maxWidth={624}>
      <SubscribeGeyser status={confettiStatus} mode="burst" />
      <StyledJosh
        colorModeOverride="dark"
        mood={status === 'success' ? 'very-happy' : 'happy'}
      />
      {status === 'success' ? (
        <SuccessWrapper ref={successRef}>
          <Title>Brilliant! You’ve been subscribed. 🎉</Title>
          <Description>
            In a few moments, you’ll receive a welcome email
            containing a collection of some of my most popular free
            content.
          </Description>
          <Description>Welcome aboard!</Description>
        </SuccessWrapper>
      ) : (
        <>
          <Title>
            A front-end web development newsletter that sparks joy ✨
          </Title>
          <Description>
            My main focus right now is teaching front-end developers
            how to do cool stuff. My personal newsletter is sent once
            every few weeks, and includes{' '}
            <Spicy>bonus subscriber-only content</Spicy>.
          </Description>
          <Description>
            No spam, no nonsense. Unsubscribe at any time.
          </Description>
          <Form onSubmit={handleSubmit}>
            {honeypotElem}

            <InputWrapper>
              <EmailLabel htmlFor={emailId}>
                Your email address:
              </EmailLabel>

              <EmailInput id={emailId} {...emailFields} />
            </InputWrapper>

            <SubmitButton data-disabled={status === 'submitting'}>
              {status === 'submitting' ? <Spinner /> : 'Subscribe!'}
            </SubmitButton>
          </Form>

          <ErrorWrapper>
            <ErrorMessage
              error={error}
              errorMinHeight="5.0625rem"
              numOfEncounteredErrors={numOfEncounteredErrors}
              status={status}
            />
          </ErrorWrapper>
        </>
      )}
    </Wrapper>
  );
}

const Wrapper = styled(MaxWidthWrapper)`
  /* Make sure to sit in front of the absolutely-positioned Slopes background */
  position: relative;
  margin-bottom: 8rem;

  /* prettier-ignore */
  filter:
    drop-shadow(0px 1px 2px hsl(210deg 40% 6% / 0.4))
    drop-shadow(0px 4px 8px hsl(210deg 40% 6% / 0.4));
`;

const StyledJosh = styled(JoshMascot)`
  position: absolute;
  top: 36px;
  left: 0;
  transform: translateX(calc(-100% - 24px));
  transition: transform 300ms;

  @media (max-width: 57rem) {
    transform: translateX(calc(-100% - 48px)) rotate(40deg);
  }
  @media (max-width: 51rem) {
    display: none;
  }
`;

const Title = styled.h2`
  max-width: 36rem;
  font-size: calc(28 / 16 * 1rem);
  margin-bottom: 1.5rem;
  line-height: 1.5;
  text-wrap: pretty;
`;
const Description = styled(Paragraph)``;

const Form = styled.form`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  margin-top: 4rem;

  @media (max-width: 25rem) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const InputWrapper = styled.div`
  flex: 1;
`;

const EmailLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-size: 1.125rem;
  font-weight: var(--font-weight-medium);
`;

const EmailInput = styled(TextInput)`
  display: block;
  width: 100%;
  height: 3rem;
  font-size: 1.125rem;
  outline-offset: 0px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &::placeholder {
    color: hsl(260deg 100% 90% / 0.75);
  }
`;
const SubmitButton = styled(ButtonPoofy)`
  --poofy-dark: hsl(230deg 50% 24%);
  --poofy-light: hsl(260deg 80% 40%);
  min-width: 9rem;
  height: 3rem;

  @media (max-width: 25rem) {
    height: 4rem;
    margin-left: -2px;
    margin-right: -2px;
  }
`;

const ErrorMessage = styled(SubscribeError)`
  --color-primary: hsl(340deg 100% 80%);

  display: block;
  padding: 16px;
  border-radius: 6px;
  background: hsl(340deg 100% 40%);

  p {
    font-size: 1rem;
    text-align: center;
    color: white;

    &:not(:last-child) {
      margin-bottom: 0.5em;
    }
  }
`;

const ErrorWrapper = styled.div`
  margin-top: 2rem;
  /*
    Prevent layout shift when error first appears.
    This is a bit redundant with errorMinHeight, but this way, we won't even have that first layout shift.
  */
  min-height: 5.0625rem;
`;

const SuccessWrapper = styled.div`
  animation: fadeIn 1250ms;
  min-height: 23.875rem;
  scroll-margin-top: 64px;
`;

export default GeneratorNewsletterSignup;
